// 支付和权限服务
import { getUserId } from '../utils/userId';
import { useUserStore } from '../store';

export interface UserAccessData {
  userId: string;
  accessLevel: 'free' | 'pro' | 'premium';
  hasProAccess: boolean;
  hasPremiumAccess: boolean;
  subscriptionId?: string;
  expiresAt?: string;
  totalPayments?: number;
  lastPaymentAt?: string;
  features: {
    aiAssistant: boolean;
    sleepAnalytics: boolean;
    customPlans: boolean;
    deviceIntegrations: boolean;
    smartNotifications: boolean;
    prioritySupport: boolean;
    advancedMoonTracking: boolean;
    sleepCoaching: boolean;
    customScenarios: boolean;
    dataExport: boolean;
    apiAccess: boolean;
    whiteLabelOptions: boolean;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class PaymentService {
  private baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '';
  private cache = new Map<string, { data: UserAccessData; timestamp: number }>();
  private cacheTimeout = 5 * 60 * 1000; // 5分钟缓存

  /**
   * 获取用户权限信息
   */
  async getUserAccess(userId?: string): Promise<UserAccessData> {
    const targetUserId = userId || getUserId();
    const cacheKey = `user_access_${targetUserId}`;
    
    // 检查缓存
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    // 在无后端（静态站点）时，直接返回默认 Free，避免解析 HTML 报错与控制台噪音
    if (!this.baseUrl) {
      return {
        userId: targetUserId,
        accessLevel: 'free',
        hasProAccess: false,
        hasPremiumAccess: false,
        totalPayments: 0,
        features: {
          aiAssistant: false,
          sleepAnalytics: false,
          customPlans: false,
          deviceIntegrations: false,
          smartNotifications: false,
          prioritySupport: false,
          advancedMoonTracking: false,
          sleepCoaching: false,
          customScenarios: false,
          dataExport: false,
          apiAccess: false,
          whiteLabelOptions: false
        }
      };
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/user/access?userId=${encodeURIComponent(targetUserId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<UserAccessData> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch user access');
      }

      // 更新缓存
      this.cache.set(cacheKey, {
        data: result.data,
        timestamp: Date.now()
      });

      return result.data;
    } catch (error) {
      console.error('获取用户权限失败:', error);
      
      // 返回默认的免费用户权限
      const defaultAccess: UserAccessData = {
        userId: targetUserId,
        accessLevel: 'free',
        hasProAccess: false,
        hasPremiumAccess: false,
        totalPayments: 0,
        features: {
          aiAssistant: false,
          sleepAnalytics: false,
          customPlans: false,
          deviceIntegrations: false,
          smartNotifications: false,
          prioritySupport: false,
          advancedMoonTracking: false,
          sleepCoaching: false,
          customScenarios: false,
          dataExport: false,
          apiAccess: false,
          whiteLabelOptions: false
        }
      };

      return defaultAccess;
    }
  }

  /**
   * 刷新用户权限信息（清除缓存并重新获取）
   */
  async refreshUserAccess(userId?: string): Promise<UserAccessData> {
    const targetUserId = userId || getUserId();
    const cacheKey = `user_access_${targetUserId}`;
    
    // 清除缓存
    this.cache.delete(cacheKey);
    
    return this.getUserAccess(targetUserId);
  }

  /**
   * 同步用户权限到本地状态管理
   */
  async syncUserAccess(userId?: string): Promise<void> {
    try {
      const accessData = await this.refreshUserAccess(userId);
      const userStore = useUserStore.getState();
      
      // 更新用户订阅信息
      if (accessData.subscriptionId || accessData.hasProAccess || accessData.hasPremiumAccess) {
        userStore.setSubscription({
          id: accessData.subscriptionId || 'unknown',
          plan: accessData.accessLevel as any,
          status: accessData.hasProAccess || accessData.hasPremiumAccess ? 'active' : 'inactive',
          startDate: accessData.lastPaymentAt || new Date().toISOString(),
          endDate: accessData.expiresAt,
          autoRenew: true,
          paymentMethod: 'creem'
        });
      }
    } catch (error) {
      console.error('同步用户权限失败:', error);
    }
  }

  /**
   * 检查用户是否有特定功能的访问权限
   */
  async hasFeatureAccess(feature: keyof UserAccessData['features'], userId?: string): Promise<boolean> {
    try {
      const accessData = await this.getUserAccess(userId);
      return accessData.features[feature] || false;
    } catch (error) {
      console.error('检查功能权限失败:', error);
      return false;
    }
  }

  /**
   * 处理支付成功回调
   */
  async handlePaymentSuccess(urlParams: URLSearchParams): Promise<void> {
    try {
      const paymentId = urlParams.get('payment_id');
      const sessionId = urlParams.get('session_id');
      
      if (paymentId || sessionId) {
        console.log('支付成功，刷新用户权限...');
        
        // 延迟一下以确保webhook已处理
        setTimeout(async () => {
          await this.syncUserAccess();
        }, 2000);
      }
    } catch (error) {
      console.error('处理支付成功回调失败:', error);
    }
  }

  /**
   * 处理支付取消
   */
  handlePaymentCancel(urlParams: URLSearchParams): void {
    console.log('用户取消了支付');
    // 可以在这里添加分析跟踪或显示特定消息
  }

  /**
   * 清除所有缓存
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * 获取支付状态消息
   */
  getPaymentStatusMessage(status: string): { message: string; type: 'success' | 'error' | 'info' } {
    switch (status) {
      case 'success':
        return {
          message: 'Payment successful! Your account has been upgraded. Enjoy your new features!',
          type: 'success'
        };
      case 'cancel':
        return {
          message: 'Payment was cancelled. You can try again anytime to upgrade your account.',
          type: 'info'
        };
      default:
        return {
          message: 'Payment status unknown. Please contact support if you need assistance.',
          type: 'error'
        };
    }
  }
}

// 导出单例实例
export const paymentService = new PaymentService();

// React Hook for easier integration
export function usePaymentService() {
  return {
    getUserAccess: paymentService.getUserAccess.bind(paymentService),
    refreshUserAccess: paymentService.refreshUserAccess.bind(paymentService),
    syncUserAccess: paymentService.syncUserAccess.bind(paymentService),
    hasFeatureAccess: paymentService.hasFeatureAccess.bind(paymentService),
    handlePaymentSuccess: paymentService.handlePaymentSuccess.bind(paymentService),
    handlePaymentCancel: paymentService.handlePaymentCancel.bind(paymentService),
    clearCache: paymentService.clearCache.bind(paymentService),
    getPaymentStatusMessage: paymentService.getPaymentStatusMessage.bind(paymentService)
  };
}
